import { Injectable } from "@angular/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import {
  BackgroundLocationService,
  LocationAuthorizationStatus,
} from "./background-location.service";

export type GuidePermissionKey =
  | "location"
  | "notifications"
  | "battery"
  | "autostart"
  | "displayPopups";

export interface GuidePermissionState {
  locationStatus: LocationAuthorizationStatus;
  notificationsGranted: boolean;
  batteryOptimizationIgnored: boolean;
  autostartEnabled: boolean;
  displayPopupsEnabled: boolean;
}

@Injectable({
  providedIn: "root",
})
export class PermissionManagerService {
  constructor(private backgroundLocationService: BackgroundLocationService) {}

  async checkPermissionState(): Promise<GuidePermissionState> {
    const [locationStatus, notificationsGranted, batteryOptimizationIgnored] =
      await Promise.all([
        this.backgroundLocationService
          .getLocationAuthorizationStatus()
          .catch(() => "denied" as LocationAuthorizationStatus),
        this.isNotificationsGranted().catch(() => false),
        this.backgroundLocationService.checkBatteryOptimization().catch(() => false),
      ]);

    return {
      locationStatus,
      notificationsGranted,
      batteryOptimizationIgnored,
      autostartEnabled: false,
      displayPopupsEnabled: await this.backgroundLocationService.hasOverlayPermission().catch(() => false),
    };
  }

  async requestPermission(key: GuidePermissionKey): Promise<void> {
    switch (key) {
      case "location": {
        // Route through native guide to reach the permission page flow first.
        const status = await this.backgroundLocationService
          .getLocationAuthorizationStatus()
          .catch(() => "denied" as LocationAuthorizationStatus);
        if (status === "notDetermined") {
          await this.backgroundLocationService.requestPermissions().catch(() => false);
        }
        await this.backgroundLocationService.openPermissionGuide("Localisation GPS");
        return;
      }
      case "notifications":
        await this.requestNotifications();
        if (!(await this.isNotificationsGranted().catch(() => false))) {
          await this.openAppSettings();
        }
        return;
      case "battery":
        await this.backgroundLocationService.requestBatteryOptimizationDisable();
        return;
      case "autostart":
        await this.backgroundLocationService.openPermissionGuide("Demarrage automatique constructeur");
        return;
      case "displayPopups":
        await this.backgroundLocationService.openPermissionGuide("Afficher les fenêtres contextuelles");
        return;
    }
  }

  isGranted(key: GuidePermissionKey, state: GuidePermissionState): boolean {
    switch (key) {
      case "location":
        return state.locationStatus === "always";
      case "notifications":
        return state.notificationsGranted;
      case "battery":
        return state.batteryOptimizationIgnored;
      case "autostart":
        return state.autostartEnabled;
      case "displayPopups":
        return state.displayPopupsEnabled;
    }
  }

  private async requestNotifications(): Promise<void> {
    if (Capacitor.getPlatform() === "web") return;
    const current = await PushNotifications.checkPermissions();
    if (current.receive === "granted") return;
    await PushNotifications.requestPermissions();
  }

  private async isNotificationsGranted(): Promise<boolean> {
    if (Capacitor.getPlatform() === "web") return true;
    try {
      const permissions = await PushNotifications.checkPermissions();
      return permissions.receive === "granted";
    } catch {
      return false;
    }
  }

  private async openAppSettings(): Promise<void> {
    if (Capacitor.getPlatform() === "web") return;
    try {
      await this.backgroundLocationService.openAppLocationSettings();
    } catch {
      // no-op fallback
    }
  }
}
